import React, { useState, useEffect, useCallback, useRef } from 'react'
import { IBanner } from '@/types/banners'
import { useBanner, BANNER_STATES } from '@/hooks/banners/useBanner'
import { useTagManager } from '@/hooks/analytics/useTagManager'
import { NoticeBanner } from './NoticeBanner'
import { OfferBanner } from './OfferBanner'

import classNames from 'classnames'
import classes from './styles.module.scss'

interface IBannerProps {
  bannerProps: IBanner
  enabled?: boolean
  // Lets the parent re-run banner selection so the freed slot is refilled
  onDismissed?: () => void
}

export const Banner: React.FC<IBannerProps> = (props) => {
  const { bannerProps, enabled = false, onDismissed } = props
  const bannerType = bannerProps.type
  const shouldShow = bannerType !== 'offer'
  const [isEnabled, setIsEnabled] = useState(shouldShow)
  const { sendGtm } = useTagManager()
  const hasSentImpressionRef = useRef(false)

  const {
    isVisible,
    dismissBanner,
    showBanner,
    bannerState
  } = useBanner({
    id: bannerProps._id,
    version: bannerProps.version,
    dismissDurationDays: bannerProps.dismissDurationDays,
    enabled: enabled && isEnabled
  })

  const handleEnableBanner = useCallback(() => {
    setIsEnabled(true)
  }, [])

  const handleDismiss = useCallback(() => {
    sendGtm.UI_BANNER_DISMISSED({ id: bannerProps._id })
    dismissBanner()
    onDismissed?.()
  }, [dismissBanner, onDismissed, sendGtm, bannerProps._id])

  useEffect(() => {
    if (
      bannerState === BANNER_STATES.UNSET &&
      shouldShow
    ) {
      showBanner()
    }
  }, [bannerState, showBanner, shouldShow])

  // Impressions are keyed on visibility, not on the UNSET → SHOWN transition:
  // the shown record never expires, so on later mounts that transition no
  // longer happens and the impression would be lost. Offer banners also only
  // become visible once their own effect resolves the special offer.
  useEffect(() => {
    if (!isVisible || hasSentImpressionRef.current) return
    hasSentImpressionRef.current = true
    sendGtm.UI_BANNER_VIEWED({ id: bannerProps._id })
  }, [isVisible, sendGtm, bannerProps._id])

  // Type-based dispatch: a `type` without a renderer (e.g. 'form') renders nothing.
  const renderContent = () => {
    switch (bannerProps.type) {
    case 'notice':
      return (
        <NoticeBanner
          dismissBanner={handleDismiss}
          bannerProps={bannerProps}
        />
      )
    case 'offer':
      return (
        <OfferBanner
          handleEnableBanner={handleEnableBanner}
          dismissBanner={handleDismiss}
          bannerProps={bannerProps}
          bannerState={bannerState}
          showBanner={showBanner}
        />
      )
    default:
      return null
    }
  }

  if (bannerState === BANNER_STATES.DISMISSED) return null
  const content = renderContent()
  if (!content) return null
  // Offer banners stay mounted while hidden: OfferBanner itself decides
  // visibility once the special-offer data resolves.
  if (bannerType !== 'offer' && !isVisible) return null

  return (
    <div
      className={classNames({
        [classes.banner]: true,
        [classes.offer]: bannerType === 'offer',
        [classes.notice]: bannerType === 'notice',
        [classes['banner--hidden']]: !isVisible
      })}
      style={isVisible ? bannerProps?.banner?.style : {}}
    >
      {content}
    </div>
  )
}
